import React from 'react';

import { AddCircleOutline, DeleteOutline } from '@mui/icons-material';
import {
  Box,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { ImageObject } from '../../pages/types';

interface ImageGalleryEditorProps {
  images?: (string | ImageObject)[];
  rowHeight?: string;
  columns?: number;
  minColumns?: number;
  gap?: string;
  onPropChange: (key: string, value: any) => void;
  openImageSelector: (index: number) => void;
  editImageIndex: number | null;
  setEditImageIndex: (index: number | null) => void;
  handleRemoveImage: (index: number) => void;
  handleUpdateImageMetadata: (
    index: number,
    field: 'title' | 'altText',
    value: string
  ) => void;
}

const ImageGalleryEditor: React.FC<ImageGalleryEditorProps> = ({
  images = [],
  rowHeight,
  columns = 3,
  minColumns,
  gap = '8px',
  onPropChange,
  openImageSelector,
  editImageIndex,
  setEditImageIndex,
  handleRemoveImage,
  handleUpdateImageMetadata,
}) => {
  // Helper functions for images
  const getImageUrl = (image: string | ImageObject): string => {
    if (typeof image === 'string') return image;
    return image.url;
  };

  const getImageAlt = (image: string | ImageObject): string => {
    if (typeof image === 'string') return `Image`;
    return image.altText || `Image`;
  };

  return (
    <>
      <Typography variant="h6">Content Options</Typography>
      <List sx={{ width: '100%', alignItems: 'left' }}>
        {images.map((image, index) => (
          <ListItem
            key={index}
            component="div"
            sx={{ flexDirection: 'column', alignItems: 'flex-start' }}
          >
            <Box display="flex" width="100%" alignItems="center">
              <img
                src={getImageUrl(image)}
                alt={getImageAlt(image)}
                style={{
                  width: '50px',
                  height: '50px',
                  marginRight: '10px',
                  objectFit: 'cover',
                }}
              />
              <ListItemButton onClick={() => openImageSelector(index)}>
                <ListItemText primary="Change Image" />
              </ListItemButton>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => handleRemoveImage(index)}
              >
                <DeleteOutline />
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() =>
                  setEditImageIndex(editImageIndex === index ? null : index)
                }
                sx={{ ml: 1 }}
              >
                {editImageIndex === index ? 'Hide Details' : 'Edit Details'}
              </Button>
            </Box>

            {editImageIndex === index && (
              <Box sx={{ ml: 8, mt: 1, width: '100%' }}>
                <TextField
                  label="Image Title"
                  value={typeof image === 'object' ? image.title || '' : ''}
                  onChange={(e) =>
                    handleUpdateImageMetadata(index, 'title', e.target.value)
                  }
                  fullWidth
                  margin="dense"
                  size="small"
                />
                <TextField
                  label="Alt Text"
                  value={typeof image === 'object' ? image.altText || '' : ''}
                  onChange={(e) =>
                    handleUpdateImageMetadata(index, 'altText', e.target.value)
                  }
                  fullWidth
                  margin="dense"
                  size="small"
                />
              </Box>
            )}
          </ListItem>
        ))}
      </List>
      <Button
        variant="contained"
        color="primary"
        onClick={() => openImageSelector(images.length)}
      >
        <AddCircleOutline />
      </Button>
      <Tooltip title="Desktop: full columns, Tablet: half columns, Mobile: 1 column unless overridden">
        <TextField
          label="Columns (Desktop)"
          type="number"
          value={columns}
          onChange={(e) =>
            onPropChange('columns', parseInt(e.target.value) || 3)
          }
          fullWidth
          margin="normal"
          inputProps={{ min: 1, max: 6 }}
        />
      </Tooltip>
      <Tooltip title="Override mobile columns (normally 1). Useful for forcing more columns on mobile.">
        <TextField
          label="Min Columns (Mobile Override)"
          type="number"
          value={minColumns || ''}
          onChange={(e) =>
            onPropChange(
              'minColumns',
              e.target.value ? parseInt(e.target.value) : undefined
            )
          }
          fullWidth
          margin="normal"
          inputProps={{ min: 1, max: 4 }}
        />
      </Tooltip>
      <Tooltip title="Set the height of each row (e.g., 200px, 15vh, auto)">
        <TextField
          label="Row Height"
          value={rowHeight || 'auto'}
          onChange={(e) => onPropChange('rowHeight', e.target.value)}
          fullWidth
          margin="normal"
        />
      </Tooltip>
      <Tooltip title="Spacing between images (e.g., 8px, 1rem, 2px)">
        <TextField
          label="Gap"
          value={gap || '8px'}
          onChange={(e) => onPropChange('gap', e.target.value)}
          fullWidth
          margin="normal"
        />
      </Tooltip>
    </>
  );
};

export default ImageGalleryEditor;
